import Core from '@mediaplatform/sportal365-tennis-sdk';
import format from 'date-fns/format';
import { ALL, ODDS } from '../constants/statusFilters';
import { RankingScopeTypes } from '../constants/tennis';
import { ITennisMatchApi, OddFormat } from '../types';
import { FIXTURES } from '../constants/programmeStatus';

const DEFAULT_DATE_FORMAT = 'dd.MM.yyyy HH:mm';

export default (sdk: Core): ITennisMatchApi => {
  const getMatchById: ITennisMatchApi['getMatchById'] = ({
    dataMatchId,
    dataOddsDisplay,
    dataOddsBettingId,
    dataOddsPreEventOnly,
    dataOddsMarket,
    dataOddsMarketValueType,
  }) => {
    if (dataMatchId) {
      const match = sdk.match(dataMatchId);

      if (dataOddsDisplay) {
        match.withOdds({
          bettingId: dataOddsBettingId,
          oddFormat: dataOddsMarketValueType,
          preEventOddsOnly: dataOddsPreEventOnly,
          market: dataOddsMarket,
        });
      }

      return match.get();
    }

    return Promise.reject(new Error('Promise fail. Check your SDK options configuration.'));
  };

  const getLivescore: ITennisMatchApi['getLivescore'] = ({
    dataOddsBettingId,
    dataOddsMarket,
    dataOddsMarketValueType,
    dateFilter,
    statusFilter,
    dataOddsDisplay,
    dataOddsPreEventOnly,
    dataPopularList,
  }) => {
    const date = dateFilter && format(dateFilter, 'yyyy-MM-dd');
    const matches = sdk.livescore({});
    if (matches) {
      matches.groupMatchesByTournament().withDateConfig({ date, dateFormat: DEFAULT_DATE_FORMAT });

      if (dataOddsDisplay) {
        matches.withOdds({
          bettingId: dataOddsBettingId || '',
          oddFormat: dataOddsMarketValueType,
          preEventOddsOnly: dataOddsPreEventOnly,
          showWithOddsOnly: statusFilter === ODDS,
          market: dataOddsMarket,
        });
      }

      if (statusFilter && ![ALL, ODDS].includes(statusFilter)) {
        matches.withStatusFilter({
          statusFilter,
          competitionList: dataPopularList,
        });
      }

      return matches.get();
    }

    return Promise.reject(new Error('Promise fail. Check your SDK options configuration.'));
  };

  const getMatches: ITennisMatchApi['getMatches'] = ({
    dataOddsBettingId,
    dataOddsMarket,
    dataOddsMarketValueType,
    dateFilter,
    statusFilter,
    dataOddsDisplay,
    dataOddsPreEventOnly,
    dataPopularList,
  }) => {
    const date = dateFilter && format(dateFilter, 'yyyy-MM-dd');
    const matches = sdk.matches();
    if (matches) {
      matches.groupMatchesByTournament().withDateConfig({ date, dateFormat: DEFAULT_DATE_FORMAT });

      if (dataOddsDisplay) {
        matches.withOdds({
          bettingId: dataOddsBettingId || '',
          oddFormat: dataOddsMarketValueType,
          preEventOddsOnly: dataOddsPreEventOnly,
          showWithOddsOnly: statusFilter === ODDS,
          market: dataOddsMarket,
        });
      }

      if (statusFilter && ![ALL, ODDS].includes(statusFilter)) {
        matches.withStatusFilter({
          statusFilter,
          competitionList: dataPopularList,
        });
      }

      return matches.get();
    }

    return Promise.reject(new Error('Promise fail. Check your SDK options configuration.'));
  };

  const getCompetition: ITennisMatchApi['getCompetition'] = ({ dataCompetition, season }) => {
    if (dataCompetition) {
      if (season) {
        return sdk.competition({ competitionId: dataCompetition, season })?.get();
      }

      return sdk.competition({ competitionId: dataCompetition })?.get();
    }

    return Promise.reject(new Error('Promise fail. Check your SDK options configuration.'));
  };

  const getTournaments: ITennisMatchApi['getTournaments'] = ({ playerId, competitionId }) => {
    if (playerId) {
      if (competitionId) {
        return sdk.tournaments({ playerId: playerId, competitionId: competitionId })?.get();
      }
      return sdk.tournaments({ playerId: playerId })?.get();
    }

    return Promise.reject(new Error('Promise fail. Check your SDK options configuration.'));
  };

  const getRanking: ITennisMatchApi['getRanking'] = ({ rankingId, limit, offset }) => {
    if (rankingId) {
      const ranking = sdk.ranking({ rankingId, limit, offset });

      return ranking?.get();
    }

    console.warn('No ranking id is provided');
  };

  const getAvailableRankings: ITennisMatchApi['getAvailableRankings'] = ({ standingTypeSubcategory }) => {
    return sdk.ranking()?.getAvailableRankings({ scope: RankingScopeTypes.CURRENT, standingTypeSubcategory })?.get();
  };

  const getAthleteProgramme: ITennisMatchApi['getAthleteProgramme'] = ({
    filter,
    dataOddsBettingId,
    dataOddsDisplay,
    dataPlayer = '',
    dataTournament = '',
    seasonYear = '',
    dataRoundsFilter = [],
    dataOddsMarket,
    dataLimit = '',
    dataDateFrom,
    dataDateTo,
    dataCompetition = '',
    dataSortDirectionFixtures = '',
    dataSortDirectionResults = '',
    dataOddsPreEventOnly = false,
    dataOddsMarketValueType = OddFormat.DECIMAL,
    signal,
    dataGameType = '',
  }) => {
    if (dataPlayer) {
      const programme = sdk.athleteProgramme({
        playerId: dataPlayer,
        seasonYear: !dataCompetition ? '' : seasonYear,
        competitionId: dataCompetition,
        tournamentId: dataRoundsFilter?.length > 0 ? '' : dataTournament,
        roundFilter: dataRoundsFilter,
        limit: dataLimit ?? '',
        sortDirection: filter === FIXTURES ? dataSortDirectionFixtures : dataSortDirectionResults,
        filterType: filter ? filter.toLowerCase() : '',
        matchType: dataGameType || '',
      });

      if (!programme) return;

      if (dataDateFrom || dataDateTo) {
        programme.withDateRange({
          from: dataDateFrom,
          to: dataDateTo,
        });
      }

      if (dataOddsDisplay) {
        programme.withOdds({
          bettingId: dataOddsBettingId ?? '',
          oddFormat: dataOddsMarketValueType.toUpperCase(),
          preEventOddsOnly: dataOddsPreEventOnly,
          market: dataOddsMarket as string,
        });
      }

      if (dataTournament && dataRoundsFilter?.length > 0) {
        console.warn('Filters data-tournament and data-rounds-filter cannot be submitted together!');
      }

      if (signal) {
        return programme?.get({ signal });
      } else {
        return programme?.get();
      }
    }

    console.warn('No athlete is provided');
  };

  const getTournamentProgramme: ITennisMatchApi['getTournamentProgramme'] = ({
    filter,
    dataCompetition,
    dataTournament,
    dataLimit = '',
    dataDateFrom,
    dataDateTo,
    seasonYear = '',
    dataRoundsFilter = [],
    dataSortDirectionFixtures = '',
    dataSortDirectionResults = '',
    dataOddsBettingId,
    dataOddsMarketValueType = OddFormat.DECIMAL,
    dataOddsDisplay,
    dataOddsPreEventOnly = false,
    dataOddsMarket,
    signal,
    dataGameType,
    dataGender,
  }) => {
    if (dataCompetition) {
      const programme = sdk.tournamentProgramme({
        competitionId: dataCompetition,
        tournamentId: dataRoundsFilter?.length > 0 ? '' : dataTournament,
        seasonYear,
        limit: dataLimit ?? '',
        sortDirection: filter === FIXTURES ? dataSortDirectionFixtures : dataSortDirectionResults,
        roundFilter: dataRoundsFilter,
        filterType: filter ? filter.toLowerCase() : '',
        matchType: dataGameType || '',
        gender: dataGender || '',
      });

      if (!programme) return;

      if (dataDateFrom || dataDateTo) {
        programme.withDateRange({
          from: dataDateFrom,
          to: dataDateTo,
        });
      }

      if (dataOddsDisplay) {
        programme.withOdds({
          bettingId: dataOddsBettingId ?? '',
          oddFormat: dataOddsMarketValueType.toUpperCase(),
          preEventOddsOnly: dataOddsPreEventOnly,
          market: dataOddsMarket as string,
        });
      }

      if (dataTournament && dataRoundsFilter?.length > 0) {
        console.warn('Filters data-tournament and data-rounds-filter cannot be submitted together!');
      }

      if (signal) {
        return programme?.get({ signal });
      } else {
        return programme?.get();
      }
    }
    console.warn('No competition is provided');
  };

  return {
    getMatchById,
    getMatches,
    getCompetition,
    getRanking,
    getAvailableRankings,
    getAthleteProgramme,
    getTournamentProgramme,
    getTournaments,
    getLivescore,
  };
};
